// Utility functions for consistent Indian Standard Time (IST) formatting across the receptionist dashboard

const IST_TIMEZONE = 'Asia/Kolkata';

// Format a Date object to h:mm a in IST
export const formatDateToISTTime = (date) => {
  try {
    return new Intl.DateTimeFormat('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: IST_TIMEZONE,
    }).format(date);
  } catch (e) {
    return '';
  }
};

// Detect simple time string like HH:mm or HH:mm:ss
const isSimpleTime = (s) => typeof s === 'string' && /^\d{1,2}:\d{2}(:\d{2})?$/.test(s);

// Format a time input (ISO string, Date, or HH:mm) to h:mm a in IST.
// If it's a simple HH:mm string, we do NOT shift timezone; we just prettify to h:mm a.
export const formatISTTime = (timeInput, opts = {}) => {
  const { dateContext } = opts; // optional Date used only to provide a date for ISO-less inputs

  try {
    if (!timeInput) return '';

    // If time is already a simple HH:mm/HH:mm:ss, prettify without timezone math
    if (isSimpleTime(timeInput)) {
      const [hStr, mStr] = timeInput.split(':');
      let h = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    }

    // If it's a Date instance or ISO string, convert to IST for display
    const dateObj = timeInput instanceof Date ? timeInput : new Date(timeInput);
    if (!isNaN(dateObj)) {
      return formatDateToISTTime(dateObj);
    }

    // Fallback: return as-is
    return String(timeInput);
  } catch {
    return String(timeInput);
  }
};

// Format a start-end time range into "h:mm a - h:mm a" in IST
export const formatISTRange = (start, end, opts = {}) => {
  const startLabel = formatISTTime(start, opts);
  const endLabel = formatISTTime(end, opts);
  return `${startLabel} - ${endLabel}`.trim();
};

// Format full date in Indian locale (no timezone shifting needed for calendar dates)
export const formatISTDateLong = (date) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: IST_TIMEZONE,
    }).format(d);
  } catch {
    return '';
  }
};

// Format date-time to readable IST (used for appointment cards)
export const formatISTDateTimeShort = (dateTime) => {
  try {
    const d = dateTime instanceof Date ? dateTime : new Date(dateTime);
    return new Intl.DateTimeFormat('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: IST_TIMEZONE,
    }).format(d);
  } catch {
    return '';
  }
};

// Short date format like 18 Oct 2025 in IST
export const formatISTDateShort = (date) => {
  try {
    const d = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: IST_TIMEZONE,
    }).format(d);
  } catch {
    return '';
  }
};

export default {
  formatISTTime,
  formatISTRange,
  formatISTDateLong,
  formatISTDateTimeShort,
  formatISTDateShort,
};
